import { debounce } from "lodash-es";
import { onBeforeUnmount, onMounted, ref } from "vue";

/**
 * Provides reactive values for client height and width properties
 */
export const useClientSize = () => {
  const clientWidth = ref(document.documentElement.clientWidth);
  const clientHeight = ref(document.documentElement.clientHeight);

  const handleResizeEvent = () => {
    clientWidth.value = document.documentElement.clientWidth;
    clientHeight.value = document.documentElement.clientHeight;
  };

  const handleResizeEventDebounced = debounce(handleResizeEvent, 300);

  onMounted(() => {
    window.addEventListener("resize", handleResizeEventDebounced);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResizeEventDebounced);
  });

  return {
    clientWidth,
    clientHeight,
  };
};

/**
 * Allows toggling and current state of fullscreen mode
 */
export const useFullscreen = () => {
  const isFullscreen = ref(false);
  const prefixes = ["", "ms", "webkit"];

  const documentElementFullscreenExtended =
    document.documentElement as HTMLElement & {
      mozRequestFullScreen: () => Promise<void>;
      webkitRequestFullscreen: () => Promise<void>;
      msRequestFullscreen: () => Promise<void>;
    };

  const documentFullscreenExtended = document as Document & {
    webkitExitFullscreen: () => Promise<void>;
    mozCancelFullScreen: () => Promise<void>;
    msExitFullscreen: () => Promise<void>;

    webkitFullscreenElement: Element | null;
    mozFullScreenElement: Element | null;
    msFullscreenElement: Element | null;
  };

  const isFullscreenSupported = () => {
    if (!documentElementFullscreenExtended || !documentFullscreenExtended) {
      return false;
    }
    // Supported by most modern browsers
    return !!(
      documentElementFullscreenExtended.requestFullscreen ||
      documentElementFullscreenExtended.webkitRequestFullscreen ||
      documentElementFullscreenExtended.msRequestFullscreen
    );
  };

  const toggleFullscreen = () => {
    if (!isFullscreenSupported()) {
      return;
    }

    if (isFullscreen.value) {
      closeFullscreen();
    } else {
      openFullscreen();
    }
  };

  const openFullscreen = () => {
    try {
      if (documentElementFullscreenExtended.requestFullscreen) {
        documentElementFullscreenExtended.requestFullscreen();
      } else if (documentElementFullscreenExtended.webkitRequestFullscreen) {
        /* Safari */
        documentElementFullscreenExtended.webkitRequestFullscreen();
      } else if (documentElementFullscreenExtended.msRequestFullscreen) {
        /* IE11 */
        documentElementFullscreenExtended.msRequestFullscreen();
      }
    } catch (_) {
      // Ignore fullscreen errors
    }
  };

  const closeFullscreen = () => {
    try {
      if (documentFullscreenExtended.exitFullscreen) {
        documentFullscreenExtended.exitFullscreen();
      } else if (documentFullscreenExtended.webkitExitFullscreen) {
        /* Safari */
        documentFullscreenExtended.webkitExitFullscreen();
      } else if (documentFullscreenExtended.msExitFullscreen) {
        /* IE11 */
        documentFullscreenExtended.msExitFullscreen();
      }
    } catch (_) {
      // Ignore fullscreen errors
    }
  };

  const handleFullscreenChange = () => {
    isFullscreen.value = !!(
      documentFullscreenExtended.fullscreenElement ||
      documentFullscreenExtended.webkitFullscreenElement ||
      documentFullscreenExtended.mozFullScreenElement ||
      documentFullscreenExtended.msFullscreenElement
    );
  };

  onMounted(() => {
    handleFullscreenChange();
    for (const prefix of prefixes) {
      document.addEventListener(
        `${prefix}fullscreenchange`,
        handleFullscreenChange
      );
    }
  });

  onBeforeUnmount(() => {
    for (const prefix of prefixes) {
      document.removeEventListener(
        `${prefix}fullscreenchange`,
        handleFullscreenChange
      );
    }
  });

  return {
    isFullscreen,
    toggleFullscreen,
    isFullscreenSupported,
    closeFullscreen,
    openFullscreen,
  };
};
